import React, { useContext, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { SaveAlt } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { ExportContext } from "../../context/ExportProvider";
import useApiEp from "../../hooks/useApiEp";
import { getErrorResponse } from "../../utils/util";
import { buildOesExportData, getOesFormOptions } from "../../utils/oesExport";
import { getCollectionRows } from "../../utils/exportPreview";

const getSubmissions = (payload) =>
  Array.isArray(payload?.oesSubmissions)
    ? payload.oesSubmissions
    : getCollectionRows(payload);

const OesExportDialog = ({ open, onClose, exportInProgress }) => {
  const { data, isLoading, isError, error, refetch, isFetching } = useApiEp(
    "/admin/get-all-oes-submit",
    open
  );
  const { exportOes, oesExporting } = useContext(ExportContext);
  const [formName, setFormName] = useState("");
  const [sheetName, setSheetName] = useState("Submissions");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const submissions = useMemo(() => getSubmissions(data), [data]);
  const forms = useMemo(() => getOesFormOptions(submissions), [submissions]);
  const selectedForm = forms.find((form) => form.name === formName);
  const sheets = useMemo(
    () => buildOesExportData(submissions, formName),
    [submissions, formName]
  );
  const activeSheet = sheets.find((sheet) => sheet.name === sheetName) || sheets[0];
  const currentPage = Math.min(
    page,
    Math.max(0, Math.ceil(activeSheet.rows.length / pageSize) - 1)
  );
  const rows = activeSheet.rows.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle>OES form export</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose a form to preview and export its submissions. Fields appear in
          the Submissions sheet; repeating tables have their own sheets linked
          by Submission ID.
        </Typography>
        {isLoading || (isFetching && !data) ? (
          <Skeleton height={80} />
        ) : isError ? (
          <Alert
            severity="error"
            action={<Button onClick={() => refetch()}>Retry</Button>}
          >
            {getErrorResponse(error)}
          </Alert>
        ) : !forms.length ? (
          <Alert severity="info">No OES submissions are available to export.</Alert>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <TextField
                select
                label="OES form"
                value={selectedForm ? formName : ""}
                disabled={oesExporting}
                sx={{ minWidth: 260, flex: 1 }}
                onChange={(event) => {
                  setFormName(event.target.value);
                  setSheetName("Submissions");
                  setPage(0);
                }}
              >
                <MenuItem value="" disabled>Select a form</MenuItem>
                {forms.map((form) => (
                  <MenuItem key={form.name} value={form.name}>
                    {form.name} ({form.count} {form.count === 1 ? "submission" : "submissions"})
                  </MenuItem>
                ))}
              </TextField>
              {selectedForm && sheets.length > 1 && (
                <TextField
                  select
                  label="Preview sheet"
                  value={activeSheet.name}
                  sx={{ minWidth: 220 }}
                  onChange={(event) => {
                    setSheetName(event.target.value);
                    setPage(0);
                  }}
                >
                  {sheets.map((sheet) => (
                    <MenuItem key={sheet.name} value={sheet.name}>
                      {sheet.name} ({sheet.rows.length} rows)
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
            {!selectedForm ? (
              <Alert severity="info">Select an OES form to view its data.</Alert>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {selectedForm.count} submissions · {activeSheet.name} · {activeSheet.rows.length} rows
                </Typography>
                {!activeSheet.rows.length ? (
                  <Alert severity="info">No table rows were saved for this section.</Alert>
                ) : (
                  <TableContainer
                    sx={{ maxHeight: 460, border: "1px solid #e5eaf2", borderRadius: 2 }}
                  >
                    <Table stickyHeader size="small" aria-label="OES export preview">
                      <TableHead>
                        <TableRow>
                          {activeSheet.columns.map((column) => (
                            <TableCell
                              key={column}
                              sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                            >
                              {column}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row, index) => (
                          <TableRow key={`${currentPage}-${index}`} hover>
                            {activeSheet.columns.map((column) => (
                              <TableCell
                                key={column}
                                sx={{ minWidth: 140, maxWidth: 360, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                              >
                                {row[column] ?? "—"}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                {activeSheet.rows.length > 0 && (
                  <TablePagination
                    component="div"
                    count={activeSheet.rows.length}
                    page={currentPage}
                    rowsPerPage={pageSize}
                    rowsPerPageOptions={[5, 10, 25]}
                    onPageChange={(_, nextPage) => setPage(nextPage)}
                    onRowsPerPageChange={(event) => {
                      setPageSize(Number(event.target.value));
                      setPage(0);
                    }}
                  />
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <LoadingButton
          variant="contained"
          startIcon={<SaveAlt />}
          loading={oesExporting}
          disabled={!selectedForm || isLoading || isError || (exportInProgress && !oesExporting)}
          onClick={() => exportOes(formName, submissions)}
          sx={{ textTransform: "none" }}
        >
          Export selected form
        </LoadingButton>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default OesExportDialog;
